import debug from 'debug';
import BaseSubClass from 'Global/Shared/Components/BaseComponent/BaseSubClass';
import ComponentStorage from 'Global/Shared/Components/ComponentStorage/ComponentStorage';
import { domService } from 'Global/Shared/Components/DomService/DomService';
import { queryString } from 'Global/Shared/Components/QueryString/QueryString';
import Breakpoints from 'Minetilbud/Shared/Components/Breakpoints/Breakpoints';
import Categories from '../data/categories';
import Behaviour from '../static/behaviour';
import Templates from '../templates/category-filter-templates';
import DataLayer from '../utils/data-layer';
import SortingType from './sorting-type';

const log = debug('FrontPageCatalogs:Render:CategoryFilter');
const Breakpoint = new Breakpoints();
const templates = new Templates();

class CategoryFilter extends BaseSubClass {

	/**
	 * @param {Object} options
	 * @param {element} options.catalogsElement The element where catalog markup is inside
	 * @param {element} options.componentElement The element that contains the entire component
	 * @param {element} options.filterElement The element where filter markup is inside
	 */
	constructor(options) {
		super(options.filterElement)

		this.catalogsElement = options.catalogsElement;
		this.componentElement = options.componentElement;

		this.categoriesEvent = this.categoriesHandler.bind(this);
		this.resizeEvent = this.resizeHandler.bind(this);
		this.scrollEvent = this.scrollHandler.bind(this);
		this.toggleEvent = this.toggleHandler.bind(this);

		this.componentStorage = new ComponentStorage({ componentKey: 'CustomerCatalogs' });

		this.initialize();
	}

	/**
	 * @param {Event} event 
	 */
	 categoriesHandler(event) {
		event.preventDefault();

		const categoryElement = event.target.closest('[data-js="catalogs-filter-category"]');
		if (categoryElement === null) return;

		const categoryText = categoryElement.innerText.split('(');
		const categoryName = categoryText[0].trim();

		categoryElement.classList.toggle('is-selected');
		let selectedCategoryElements = this.dom.categories.querySelectorAll('.is-selected');

		if (categoryElement.getAttribute('data-id') === 'alle') {
			[...selectedCategoryElements].map(selectedCategoryElement => selectedCategoryElement.classList.remove('is-selected'));
			categoryElement.classList.add('is-selected');
			DataLayer.filterAdd({ filterName: categoryName, filterType: 'category' });
		} else {
			const allCategoriesElement = this.dom.categories.querySelector('[data-id="alle"]');

			if (categoryElement.classList.contains('is-selected')) {
				DataLayer.filterAdd({ filterName: categoryName, filterType: 'category' });
			} else {
				DataLayer.filterRemove({ filterName: categoryName, filterType: 'category' });
			}
			
			allCategoriesElement.classList.remove('is-selected');

			if (selectedCategoryElements.length === 0) {
				allCategoriesElement.classList.add('is-selected');
				
				const allCategoryText = allCategoriesElement.innerText.split('(');
				const allCategoryName = allCategoryText[0].trim();
				DataLayer.filterAdd({ filterName: allCategoryName, filterType: 'category' });
			} 
		}

		let categoryIds = '';
		let categories = [];
		selectedCategoryElements = this.dom.categories.querySelectorAll('.is-selected');
		[...selectedCategoryElements].map(selectedCategoryElement => {
			categoryIds += selectedCategoryElement.getAttribute('data-id') + '|';
			categories.push(selectedCategoryElement.getAttribute('data-id'));
		});

		this.componentStorage.setItem('categories', categoryIds);
		queryString.Add('id', categoryIds);

		history.pushState(categoryIds, '', location.pathname + '?' + queryString.Search());

		this.dom.wrapper.classList.remove('is-fixed');
		this.dom.wrapper.style.top = '0px';

		Behaviour.dispatchEvent(Behaviour.eventKeys.filterCategory, { categories });
	}

	eventListeners() {
		window.addEventListener('resize', this.resizeEvent, false);
		document.addEventListener('scroll', this.scrollEvent, false);

		this.dom.categories.addEventListener('click', this.categoriesEvent, false);
		this.dom.toggle.addEventListener('click', this.toggleEvent, false);
	}

	async initialize() {
		await templates.dictionaryInit();

		this.setup();
		this.setClassName('catalogs-filter');
		this.initDomCache();
		this.eventListeners();

		DataLayer.filterOverview();

		if (Breakpoint.MediaBreakpointUp('lg')) {
			const sortingType = new SortingType(this.dom.container);
			this.render();
		}
	}

	render() {
		this.dom.categories.insertAdjacentHTML('beforeend', templates.categoryAll(Categories.hasSelectedCategories === false));
		Categories.all.forEach(category => {
			this.dom.categories.insertAdjacentHTML('beforeend', templates.category(category));
		});
	}

	resizeHandler() {
		if (window.innerWidth < 767) {
			this.dom.toggle.style.height = '';
		}

		if (Breakpoint.MediaBreakpointUp('lg') && this.dom.categories.children.length === 0) {
			const sortingType = new SortingType(this.dom.container);
			this.render();
		}
	}

	/**
	 * @param {Event} event 
	 */
	scrollHandler(event) {
		// If not tablet og PC return
		if (window.innerWidth < 767 || domService.IE11()) {
			this.unstick();
			return;
		}

		let catalogsRect = this.catalogsElement.getBoundingClientRect();
		let filtersRect = this.dom.wrapper.getBoundingClientRect();

		// If browser height is smaller than filter height return
		if (window.innerHeight < filtersRect.height) {
			this.unstick();
			return;
		}

		if (filtersRect.height >= (catalogsRect.height - 100)) {
			this.unstick();
			return;
		}

		if (catalogsRect.top <= 0 && filtersRect.height < catalogsRect.bottom) {
			// Scrolled past top of browser and theres enough space to display the entire filter
			this.stick();
			this.dom.wrapper.style.top = '0px';
		} else if (filtersRect.bottom >= catalogsRect.bottom || filtersRect.top < 0) {
			// Scrolled past top of browser and NOT enough space to display the entire filter
			this.stick();
			window.requestAnimationFrame(() => {
				this.dom.wrapper.style.top = (catalogsRect.bottom - filtersRect.height) + 'px';
			});
		} else {
			this.unstick();
		}

	}

	setup() {
		this.element.insertAdjacentHTML('beforeend', templates.toggle());
		this.element.insertAdjacentHTML('beforeend', templates.base());
	}

	/**
	 * Make filters stick to browser window
	 */
	stick() {
		this.dom.wrapper.classList.add('is-fixed');
	}

	/**
	 * @param {Event} event 
	 */
	toggleHandler(event) {
		event.preventDefault();

		if (this.dom.categories.children.length === 0) {
			const sortingType = new SortingType(this.dom.container);
			this.render();
		}

		this.componentElement.classList.toggle('filter-is-visible');

		const filtersRect = this.dom.wrapper.getBoundingClientRect();
		if (window.innerHeight < filtersRect.height) {
			this.element.classList.remove('is-fixed');
		};

		let catalogsRect = this.catalogsElement.getBoundingClientRect();
		if (filtersRect.height > catalogsRect.height) {
			this.dom.wrapper.classList.remove('is-fixed');
		}
	}

	/**
	 * Make filters unstick to browser window
	 */
	unstick() {
		this.dom.wrapper.classList.remove('is-fixed');
	}

}

export default CategoryFilter;
